import { useEffect, useState } from 'react'

const CHANNEL_NAME = 'kc3-tab-lock'
const PING_MSG = 'ping'
const PONG_MSG = 'pong'
const HEARTBEAT_INTERVAL = 2000

/**
 * Check if the current path is an agent portal route.
 * Single-tab restriction only applies to agent portal, not admin or client portal.
 */
function isAgentPortalRoute(): boolean {
  const path = window.location.pathname
  // Admin routes start with /admin
  if (path.startsWith('/admin')) return false
  // Client portal routes start with /portal or /client-portal
  if (path.startsWith('/portal') || path.startsWith('/client-portal')) return false
  // Platform admin routes
  if (path.startsWith('/platform')) return false
  // Login and auth routes should not be restricted
  if (path.startsWith('/login') || path.startsWith('/auth') || path.startsWith('/forgot') || path.startsWith('/reset')) return false
  // Everything else is agent portal
  return true
}

export default function SingleTabGuard({ children }: { children: React.ReactNode }): React.ReactElement {
  const [isPrimary, setIsPrimary] = useState<boolean | null>(null)

  useEffect(() => {
    // Skip in SSR / test environments
    if (typeof window === 'undefined') return

    // The email OAuth popup loads this app on /email-oauth-callback; it must
    // not be blocked as a "second tab" because it only posts back and closes.
    if (window.location.pathname.startsWith('/email-oauth-callback')) {
      setIsPrimary(true)
      return
    }

    // Only apply single-tab restriction to agent portal routes
    // Admin and client portal can be opened in multiple tabs
    if (!isAgentPortalRoute()) {
      setIsPrimary(true)
      return
    }

    // Skip inside an iframe (e.g. the Portal Configuration / Design Library
    // live-preview iframe, which loads the same SPA at /portal/:tenantSlug).
    // BroadcastChannel is shared across all same-origin browsing contexts,
    // tabs AND iframes alike — without this check, the preview iframe pings
    // the channel, the real admin tab answers, and the iframe concludes it's
    // a "second tab" and renders the lockout screen instead of the preview.
    let isEmbedded = false
    try {
      isEmbedded = window.self !== window.top
    } catch {
      // Cross-origin frame access throws — treat as embedded/non-primary
      // context, not a standalone tab.
      isEmbedded = true
    }
    if (isEmbedded) {
      setIsPrimary(true)
      return
    }

    // Safari < 15.4 doesn't support BroadcastChannel — skip guard
    if (!('BroadcastChannel' in window)) {
      setIsPrimary(true)
      return
    }

    const channel = new BroadcastChannel(CHANNEL_NAME)
    let heartbeatTimer: ReturnType<typeof setInterval>
    let pongReceived = false

    // When another tab sends a ping, respond with pong
    channel.onmessage = (event: MessageEvent) => {
      if (event.data === PING_MSG) {
        channel.postMessage(PONG_MSG)
      } else if (event.data === PONG_MSG) {
        pongReceived = true
      }
    }

    // Ping other tabs immediately on mount
    channel.postMessage(PING_MSG)

    // Wait a short delay to see if any existing tab responds
    const checkTimer = setTimeout(() => {
      if (pongReceived) {
        setIsPrimary(false)
        channel.close()
      } else {
        setIsPrimary(true)
        // We are the primary tab — start heartbeat to let new tabs know we exist
        heartbeatTimer = setInterval(() => {
          channel.postMessage(PING_MSG)
        }, HEARTBEAT_INTERVAL)
      }
    }, 300)

    return () => {
      clearTimeout(checkTimer)
      clearInterval(heartbeatTimer)
      channel.close()
    }
  }, [])

  // While checking, show blank screen — app content never flashes
  if (isPrimary === null) {
    return <div className="min-h-screen bg-gray-50" />
  }

  if (!isPrimary) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg
              className="w-8 h-8 text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Sorry! You can only have this application opened in one tab.
          </h2>
          <p className="text-gray-600 mb-6">
            Agent Portal is already open on another tab.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="w-full py-2.5 px-4 bg-[#3366FF] text-white rounded-lg hover:bg-[#2952CC] transition-colors font-medium"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return <>{children}</>
}
